import { loadProfile, saveProfile, type Profile } from '../meta/profile';
import type { BaseLayout } from '../sim/raid/bases';

/**
 * Room-server address and the profile / raid HTTP API (Phases 10–15). Every
 * call fails soft: no server, no network, or an error → null, and the game
 * keeps working offline.
 */
export function roomServer(): string | null {
  const p = new URLSearchParams(window.location.search).get('server');
  if (p && /^wss?:\/\/[\w.\-:[\]]+(\/[\w\-/]*)?$/.test(p)) return p;
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_ROOM_SERVER;
  return env && /^wss?:\/\//.test(env) ? env : null;
}

function httpBase(server: string): string {
  return server.replace(/^ws/, 'http').replace(/\/$/, '');
}

async function call<T>(server: string, path: string, init: RequestInit = {}, profile?: Profile): Promise<T | null> {
  try {
    const headers = new Headers(init.headers);
    headers.set('content-type', 'application/json');
    if (profile?.server) headers.set('authorization', `Bearer ${profile.server.id}:${profile.server.token}`);
    const r = await fetch(`${httpBase(server)}${path}`, { ...init, headers });
    return r.ok ? ((await r.json()) as T) : null;
  } catch {
    return null;
  }
}

/** The local profile, registered on the server once (guest account). */
export async function ensureServerProfile(server: string): Promise<Profile> {
  const p = loadProfile();
  if (p.server) return p;
  const r = await call<{ id: string; token: string }>(server, '/api/profile', { method: 'POST', body: JSON.stringify({ name: p.name }) });
  if (!r || !/^[0-9a-f]{16}$/.test(r.id) || !/^[0-9a-f]{32}$/.test(r.token)) return p;
  const next = { ...p, server: { id: r.id, token: r.token } };
  saveProfile(next);
  return next;
}

export async function uploadBase(server: string, base: BaseLayout): Promise<boolean> {
  const p = await ensureServerProfile(server);
  if (!p.server) return false;
  return !!(await call<{ ok: boolean }>(server, '/api/base', { method: 'PUT', body: JSON.stringify(base) }, p));
}

export interface Opponent {
  readonly id: string;
  readonly name: string;
  readonly points: number;
  readonly buildings: number;
  readonly defenders: number;
}

export async function fetchOpponents(server: string): Promise<Opponent[] | null> {
  const p = await ensureServerProfile(server);
  if (!p.server) return null;
  return (await call<{ opponents: Opponent[] }>(server, '/api/opponents', {}, p))?.opponents ?? null;
}

export async function fetchLeaderboard(server: string): Promise<{ id: string; name: string; points: number; xp: number }[] | null> {
  return (await call<{ top: { id: string; name: string; points: number; xp: number }[] }>(server, '/api/leaderboard'))?.top ?? null;
}

export async function fetchServerProfile(server: string, id: string): Promise<{ points: number; xp: number; raids: number; defences: number } | null> {
  return call(server, `/api/profile/${id}`);
}

/**
 * Campaign score for the online rankings (D111). NOTE: computed on this device,
 * so the server stores it as client-reported (capped and rate-limited) — not a
 * verified replay. Shown as such.
 */
export async function submitLevelScore(server: string, level: number, s: { stars: number; score: number }): Promise<boolean> {
  const p = await ensureServerProfile(server);
  if (!p.server) return false;
  return !!(await call<{ ok: boolean }>(server, '/api/score', { method: 'POST', body: JSON.stringify({ level, stars: s.stars, score: s.score }) }, p));
}

export interface LevelRankRow {
  readonly id: string;
  readonly name: string;
  readonly score: number;
  readonly stars: number;
}

/** Top scores for a level; `friends` limits it to you and your friends. */
export async function fetchLevelRanking(server: string, level: number, friends = false): Promise<LevelRankRow[] | null> {
  const p = friends ? await ensureServerProfile(server) : undefined;
  return (await call<{ top: LevelRankRow[] }>(server, `/api/scores?level=${level}${friends ? '&friends=1' : ''}`, {}, p))?.top ?? null;
}

/** Add a friend by their 16-character player code. */
export async function addFriend(server: string, code: string): Promise<{ ok: boolean; name?: string }> {
  const p = await ensureServerProfile(server);
  if (!p.server) return { ok: false };
  return (await call<{ ok: boolean; name?: string }>(server, '/api/friends', { method: 'POST', body: JSON.stringify({ id: code }) }, p)) ?? { ok: false };
}

/** Season-pass entitlements the server granted this profile (premium track). */
export async function fetchEntitlements(server: string): Promise<{ premium: boolean } | null> {
  const p = await ensureServerProfile(server);
  if (!p.server) return null;
  return call<{ premium: boolean }>(server, '/api/entitlements', {}, p);
}

/** Weekly Challenge score (D123): the server keeps only the first one per player per week. */
export async function submitWeeklyScore(server: string, week: string, s: { stars: number; score: number }): Promise<boolean> {
  const p = await ensureServerProfile(server);
  if (!p.server) return false;
  return !!(await call<{ ok: boolean }>(server, '/api/score', { method: 'POST', body: JSON.stringify({ week, stars: s.stars, score: s.score }) }, p));
}

export async function fetchWeeklyRanking(server: string, week: string, friends = false): Promise<LevelRankRow[] | null> {
  const p = friends ? await ensureServerProfile(server) : undefined;
  return (await call<{ top: LevelRankRow[] }>(server, `/api/scores?week=${encodeURIComponent(week)}${friends ? '&friends=1' : ''}`, {}, p))?.top ?? null;
}
